/**
 * Shared utilities for PBH status handling
 * Centralizes status type determination logic to avoid duplication
 *
 * Used by Exchanges status toolbar to label options inbound vs outbound and to
 * build `inboundStatus` / `outboundStatus` URL params (`buildStatusFilterParams`).
 * Includes SQL-only display labels from `sqlOnlyOrderMapper`.
 */

/**
 * Status type indicator
 */
export type StatusType = "inbound" | "outbound";

/**
 * Status option with type information (static Exchanges status dropdown)
 */
export interface StatusOption {
  label: string;
  value: string;
  type: StatusType;
}

/**
 * List of known inbound statuses
 * Used to determine if a status is inbound or outbound
 */
export const INBOUND_STATUSES = [
  "OffcorePart",
  "OffcoreShipping",
  "AwaitingOffCoreInformation",
  "OffCore Part Info",
  "OffCore Shipping",
  "Awaiting OffCore Info",
  "Off Core Detail Completed",
  "Off Core Detail",
] as const;

/**
 * Determine if a status is inbound or outbound
 * @param statusValue - The status value to check
 * @returns "inbound" if the status is an inbound status, "outbound" otherwise
 */
export const getStatusType = (
  statusValue: string,
): "inbound" | "outbound" => {
  const normalized = String(statusValue || "").trim();
  const isInbound = INBOUND_STATUSES.some(
    (inbound) => String(inbound || "").trim() === normalized,
  );

  return isInbound ? "inbound" : "outbound";
};

